/**
 * campaign-landing.js
 * Wholesale Shopping — B2B Campaign Landing Page
 * Modern ecommerce animations: fast, subtle, conversion-focused.
 * Removed: initHeroScroll, initStoryReveal, initParallax, initReasonStagger, initTrendStagger
 */

(function () {
  'use strict';

  /* ─── GSAP readiness poll ─────────────────────────────────────────────── */
  function whenGSAP(cb) {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      cb();
    } else {
      setTimeout(function () { whenGSAP(cb); }, 80);
    }
  }

  /* ─── Hero interactions: run immediately (no GSAP needed) ───────────── */
  document.addEventListener('DOMContentLoaded', function () {
    initHeroInteractions();
    initCountdownTimer();
  });
  /* Also run now in case DOM is already ready */
  if (document.readyState !== 'loading') {
    initHeroInteractions();
    initCountdownTimer();
  }

  whenGSAP(boot);

  /* ─── Boot ────────────────────────────────────────────────────────────── */
  function boot() {
    if (!document.getElementById('campaign-landing')) return;

    gsap.registerPlugin(ScrollTrigger);

    /* Reduced-motion: reveal everything immediately, skip all animations */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.set(
        [
          '.cl-hero__tag',
          '.cl-hero__headline',
          '.cl-hero__sub',
          '.cl-hero__stats',
          '.cl-hero__cta',
          '.cl-hero__mosaic-item',
          '[data-cl-reveal]',
          '.cl-trust__item',
          '.cl-pcard',
          '.cl-feature',
          '.cl-process__step',
          '.cl-rcard',
        ].join(','),
        { opacity: 1, y: 0, scale: 1 }
      );
      return;
    }

    initHero();
    initScrollReveal();
    initTrustReveal();
    initCardStagger();
    initViralCards();
    initFeatureReveal();
    initProcessReveal();
    initReviewsCarousel();
    initTspCarousel();
    initSeoSection();
    initStickyAtc();
  }

  /* ═══════════════════════════════════════════════════════════════════════
     HERO INTERACTIONS + GALLERY + COLOUR SHOWCASE SYNC
     All shared state declared at top so every sub-function can access it.
     No GSAP dependency — runs as soon as DOM is ready.
     ═══════════════════════════════════════════════════════════════════════ */
  function initHeroInteractions() {
    var page = document.getElementById('campaign-landing');
    if (!page) return;

    /* ── Collect all interactive elements up front ──────────────────────── */
    var swatches   = page.querySelectorAll('.cl-hero__color-swatch');
    var colorLabel = page.querySelector('.cl-hero__color-name');
    var chips      = page.querySelectorAll('.cl-color-chip');
    var mainImg    = page.querySelector('.cl-hero__gallery-main img');
    var thumbs     = page.querySelectorAll('.cl-hero__gallery-thumb');

    /* ── Variant → image map from embedded JSON (set in Liquid) ─────────── */
    var variantImgMap = {};
    var variantDataEl = document.getElementById('cl-variant-images');
    if (variantDataEl) {
      try {
        variantImgMap = JSON.parse(variantDataEl.textContent || '{}');
      } catch (e) {
        variantImgMap = {};
      }
    }

    /* ── Gallery element references ────────────────────────────────────── */
    var galleryMain      = page.querySelector('.cl-hero__gallery-main');
    var thumbsContainer  = page.querySelector('.cl-hero__gallery-thumbs');
    var railBtnUp        = page.querySelector('.cl-gallery-rail__btn--up');
    var railBtnDown      = page.querySelector('.cl-gallery-rail__btn--down');

    /* ── Rail button state: enable/disable UP/DOWN based on scroll pos ── */
    function updateRailBtnState() {
      if (!thumbsContainer) return;
      var st        = thumbsContainer.scrollTop;
      var maxScroll = thumbsContainer.scrollHeight - thumbsContainer.clientHeight;
      /* Allow 1px rounding tolerance */
      if (railBtnUp)   railBtnUp.disabled   = st <= 1;
      if (railBtnDown) railBtnDown.disabled  = st >= maxScroll - 1;
    }

    /* Scroll the strip by ~3 thumbnails per button click */
    var SCROLL_STEP = 270; /* px — approx 3 × (90px thumb + 6px gap) */

    if (railBtnUp && thumbsContainer) {
      railBtnUp.addEventListener('click', function () {
        thumbsContainer.scrollBy({ top: -SCROLL_STEP, behavior: 'smooth' });
      });
    }
    if (railBtnDown && thumbsContainer) {
      railBtnDown.addEventListener('click', function () {
        thumbsContainer.scrollBy({ top: SCROLL_STEP, behavior: 'smooth' });
      });
    }

    /* Update button states on scroll and on window resize */
    if (thumbsContainer) {
      thumbsContainer.addEventListener('scroll', updateRailBtnState, { passive: true });
      window.addEventListener('resize', updateRailBtnState, { passive: true });
      /* Initial state check after first paint */
      requestAnimationFrame(updateRailBtnState);
    }

    /* ── Scroll active thumb into view WITHIN the container ─────────────
       Uses container-relative offsetTop — does NOT trigger page scroll.
       ────────────────────────────────────────────────────────────────── */
    function scrollThumbIntoRail(thumb) {
      if (!thumbsContainer || !thumb) return;
      var containerH  = thumbsContainer.clientHeight;
      var containerST = thumbsContainer.scrollTop;
      /* offsetTop is relative to offsetParent; thumbsContainer has
         position: relative so this gives the correct in-container offset */
      var thumbTop    = thumb.offsetTop;
      var thumbBot    = thumbTop + thumb.offsetHeight;

      if (thumbTop < containerST + 4) {
        /* Thumb above visible area — scroll up */
        thumbsContainer.scrollTo({ top: thumbTop - 4, behavior: 'smooth' });
      } else if (thumbBot > containerST + containerH - 4) {
        /* Thumb below visible area — scroll down */
        thumbsContainer.scrollTo({ top: thumbBot - containerH + 4, behavior: 'smooth' });
      }

      /* Re-evaluate button states after scroll settles */
      setTimeout(updateRailBtnState, 320);
    }

    /* ── Main image swap: fade out → swap src → fade in ─────────────────── */
    function switchMainImage(fullSrc, activeThumb) {
      if (!mainImg || !fullSrc) return;

      /* 1. Fade out */
      mainImg.style.opacity = '0';

      /* 2. After fade-out starts, swap the src */
      setTimeout(function () {
        mainImg.setAttribute('src', fullSrc);
        /* 3a. On load (uncached images) */
        mainImg.onload = function () { mainImg.style.opacity = '1'; };
        /* 3b. Fallback for cached images where onload already fired */
        setTimeout(function () { mainImg.style.opacity = '1'; }, 200);
      }, 80);

      /* 4. Update active thumbnail highlight */
      thumbs.forEach(function (t) { t.classList.remove('cl-hero__gallery-thumb--active'); });
      if (activeThumb) {
        activeThumb.classList.add('cl-hero__gallery-thumb--active');
        /* Scroll within rail — NOT page scroll */
        if (window.innerWidth > 768) {
          scrollThumbIntoRail(activeThumb);
        } else {
          /* Mobile horizontal strip: use inline scroll */
          activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      }
    }

    /* ── Touch/swipe on main image (mobile carousel) ────────────────────── */
    if (galleryMain) {
      var swipeStartX = 0;
      var swipeStartY = 0;

      galleryMain.addEventListener('touchstart', function (e) {
        swipeStartX = e.touches[0].clientX;
        swipeStartY = e.touches[0].clientY;
      }, { passive: true });

      galleryMain.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - swipeStartX;
        var dy = e.changedTouches[0].clientY - swipeStartY;
        /* Only fire on horizontal swipes ≥ 40px */
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;

        var currentIdx = 0;
        thumbs.forEach(function (t, i) {
          if (t.classList.contains('cl-hero__gallery-thumb--active')) currentIdx = i;
        });

        if (dx < 0 && currentIdx < thumbs.length - 1) {
          thumbs[currentIdx + 1].click(); /* swipe left → next */
        } else if (dx > 0 && currentIdx > 0) {
          thumbs[currentIdx - 1].click(); /* swipe right → prev */
        }
      }, { passive: true });
    }

    /* ═══════════════════════════════════════════════════════════════════
       SHARED STATE — single source of truth for selected colour.
       Updates: hero label · hero swatches · showcase chips · gallery image
       source: 'swatch' | 'chip' | 'init'
       ═══════════════════════════════════════════════════════════════════ */
    function selectColour(name, source) {
      var normName = (name || '').trim().toLowerCase();

      /* 1. Update hero colour name label */
      if (colorLabel) colorLabel.textContent = name || 'Selected';

      /* 2. Sync hero swatches — live query includes dynamically-added swatches */
      page.querySelectorAll('.cl-hero__color-swatch').forEach(function (s) {
        var swatchName = (s.getAttribute('title') || '').trim().toLowerCase();
        var isMatch    = swatchName === normName;
        s.classList.toggle('cl-hero__color-swatch--active', isMatch);
        s.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
      });

      /* 3. Sync colour showcase chips — live query includes dynamically-added chips */
      page.querySelectorAll('.cl-color-chip').forEach(function (chip) {
        var nameEl   = chip.querySelector('.cl-color-chip__name');
        var chipName = nameEl ? nameEl.textContent.trim().toLowerCase() : '';
        var isMatch  = chipName === normName;
        chip.classList.toggle('cl-color-chip--active', isMatch);
        chip.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
        /* Only scroll chip into view when click originates FROM chip section */
        if (isMatch && source === 'chip') {
          chip.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });

      /* 4. Update gallery main image if variant has a linked image */
      if (source !== 'init') {
        var imgData = variantImgMap[normName];
        if (imgData && imgData.full) {
          /* Find the thumbnail whose data-full-src matches this variant */
          var matchThumb = null;
          thumbs.forEach(function (t) {
            if (!matchThumb && t.getAttribute('data-full-src') === imgData.full) {
              matchThumb = t;
            }
          });
          switchMainImage(imgData.full, matchThumb);
        }
      }
    }

    /* ── 1. Hero colour swatches ────────────────────────────────────────── */
    swatches.forEach(function (swatch) {
      swatch.setAttribute('role', 'button');
      swatch.setAttribute('tabindex', '0');
      swatch.setAttribute('aria-pressed', 'false');
      swatch.style.cursor = 'pointer';

      swatch.addEventListener('click', function () {
        clearAtcError();
        selectColour(swatch.getAttribute('title') || '', 'swatch');
      });
      swatch.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); swatch.click(); }
      });
    });

    /* ── 2. Colour showcase chips ───────────────────────────────────────── */
    chips.forEach(function (chip, idx) {
      chip.setAttribute('role', 'button');
      chip.setAttribute('tabindex', '0');
      chip.setAttribute('aria-pressed', 'false');
      chip.style.cursor = 'pointer';

      var nameEl = chip.querySelector('.cl-color-chip__name');

      chip.addEventListener('click', function () {
        var name = nameEl ? nameEl.textContent.trim() : '';
        selectColour(name, 'chip');
        /* Scroll hero panel into view so user sees the update */
        var heroPanel = page.querySelector('.cl-hero__panel');
        if (heroPanel) heroPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      chip.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); chip.click(); }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); var n = chips[idx + 1]; if (n) n.focus(); }
        if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); var p = chips[idx - 1]; if (p) p.focus(); }
      });
    });

    /* Initialise default selected colour from the pre-active swatch */
    var initialSwatch = page.querySelector('.cl-hero__color-swatch--active');
    if (initialSwatch) selectColour(initialSwatch.getAttribute('title') || '', 'init');

    /* ── 2.5. Pack Quantity Selector ────────────────────────────────────────
       Two options: Pack of 1 (£4.00/unit → qty 1) and Pack of 3 (£3.75/unit → qty 3).
       Selecting a pack:
         • Updates the wholesale price display instantly.
         • Updates the margin pill, margin bar, and margin labels instantly.
         • Updates the info message below the buttons.
         • The cart handler multiplies the qty-stepper value by selectedPackQty.
       ─────────────────────────────────────────────────────────────────────── */
    var packBtns       = page.querySelectorAll('.cl-hero__pack-btn');
    var packInfoEl     = document.getElementById('cl-pack-info');
    var priceWsEl      = document.getElementById('cl-price-ws');
    var marginPillEl   = document.getElementById('cl-margin-pill');
    var wsLabelEl      = document.getElementById('cl-ws-label');
    var marginPctLblEl = document.getElementById('cl-margin-pct-label');
    var marginBarEl    = document.getElementById('cl-margin-bar');

    /*
     * Pack of 1: £4.00/unit  → margin = (12.99 - 4.00) / 12.99 = 69%
     * Pack of 3: £3.75/unit  → margin = (12.99 - 3.75) / 12.99 = 71%
     *            total = 3 × £3.75 = £11.25
     */
    var PACK_DATA = {
      1: {
        priceStr:    '£4.00',
        marginPct:   69,
        barWidth:    69,
        wsLabel:     'Wholesale £4.00',
        marginLabel: '69% gross margin',
        cartQty:     1,
        isBetterValue: false
      },
      3: {
        priceStr:    '£3.75',
        marginPct:   71,
        barWidth:    71,
        wsLabel:     'Wholesale £3.75',
        marginLabel: '71% gross margin',
        cartQty:     3,
        isBetterValue: true
      }
    };

    var selectedPackQty = 1; /* cart units per "pack" — updated by applyPack() */

    function applyPack(packSize) {
      var d = PACK_DATA[packSize];
      if (!d) return;

      selectedPackQty = d.cartQty;

      /* ── Price display ───────────────────────────────────────── */
      if (priceWsEl)      priceWsEl.textContent      = d.priceStr + ' / unit';
      if (marginPillEl)   marginPillEl.textContent    = d.marginPct + '% Margin';
      if (wsLabelEl)      wsLabelEl.textContent       = d.wsLabel;
      if (marginPctLblEl) marginPctLblEl.textContent  = d.marginLabel;

      /* ── Margin bar (CSS custom property drives the ::after width) ── */
      if (marginBarEl) {
        marginBarEl.style.setProperty('--cl-margin-bar-w', d.barWidth + '%');
      }

      /* ── Info message ─────────────────────────────────────────── */
      if (packInfoEl) {
        packInfoEl.innerHTML =
          '<strong class="cl-hero__pack-info-price">' + d.priceStr + '</strong>' +
          ' per unit' +
          (d.isBetterValue
            ? ' <span class="cl-hero__pack-info-badge">Better Value</span>'
            : '');
      }

      /* ── Sync button active states ────────────────────────────── */
      packBtns.forEach(function (b) {
        var isMatch = parseInt(b.dataset.pack, 10) === packSize;
        b.classList.toggle('cl-hero__pack-btn--active', isMatch);
        b.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
      });

      /* ── Ratio display ───────────────────────────────────────── */
      var ratioEl = document.getElementById('cl-size-ratio');
      if (ratioEl) ratioEl.textContent = packSize;
    }

    /* Returns the number of units per pack (1 or 3) for cart qty calculation */
    function getPackQty() { return selectedPackQty; }

    if (packBtns.length) {
      packBtns.forEach(function (btn) {
        btn.style.cursor = 'pointer';
        btn.addEventListener('click', function () {
          clearAtcError();
          applyPack(parseInt(btn.dataset.pack, 10) || 1);
        });
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); btn.click(); }
        });
      });
      applyPack(1); /* initialise to Pack of 1 */
    }

    /* ── 3. Size buttons ────────────────────────────────────────────────── */
    var sizeBtns = page.querySelectorAll('.cl-hero__size-btn');
    sizeBtns.forEach(function (btn) {
      btn.style.cursor = 'pointer';
      btn.setAttribute('aria-pressed', btn.classList.contains('cl-hero__size-btn--active') ? 'true' : 'false');
      btn.addEventListener('click', function () {
        sizeBtns.forEach(function (b) { b.classList.remove('cl-hero__size-btn--active'); b.setAttribute('aria-pressed', 'false'); });
        btn.classList.add('cl-hero__size-btn--active');
        btn.setAttribute('aria-pressed', 'true');
      });
    });

    /* ── 4. Quantity +/- ────────────────────────────────────────────────── */
    var qtyVal   = page.querySelector('.cl-hero__qty-val');
    var qtyMinus = page.querySelector('.cl-hero__qty-btn[aria-label="Decrease"]');
    var qtyPlus  = page.querySelector('.cl-hero__qty-btn[aria-label="Increase"]');
    function getQty() { return parseInt(qtyVal ? qtyVal.value : '1', 10) || 1; }
    function setQty(n) { if (qtyVal) qtyVal.value = Math.max(1, n); }
    if (qtyMinus) {
      qtyMinus.style.cursor = 'pointer';
      qtyMinus.addEventListener('click', function () { clearAtcError(); setQty(getQty() - 1); });
      qtyMinus.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); qtyMinus.click(); } });
    }
    if (qtyPlus) {
      qtyPlus.style.cursor = 'pointer';
      qtyPlus.addEventListener('click', function () { clearAtcError(); setQty(getQty() + 1); });
      qtyPlus.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); qtyPlus.click(); } });
    }
    if (qtyVal) {
      qtyVal.addEventListener('input', function () {
        clearAtcError();
        var n = parseInt(qtyVal.value, 10);
        if (!n || n < 1) qtyVal.value = 1;
      });
      qtyVal.addEventListener('blur', function () {
        var n = parseInt(qtyVal.value, 10);
        if (!n || n < 1) qtyVal.value = 1;
      });
    }

    /* ── 5. Gallery thumbnails ──────────────────────────────────────────── */
    /* Uses data-full-src (set in Liquid) — no fragile URL regex needed.    */
    thumbs.forEach(function (thumb, idx) {
      thumb.style.cursor = 'pointer';
      thumb.setAttribute('role', 'button');
      thumb.setAttribute('tabindex', '0');
      thumb.setAttribute('aria-label', 'Product image ' + (idx + 1));

      thumb.addEventListener('click', function () {
        /* data-full-src is the Shopify CDN URL at display size (600x750) */
        var fullSrc = thumb.getAttribute('data-full-src');

        /* Fallback: derive from thumbnail src if data attribute missing */
        if (!fullSrc) {
          var thumbImg = thumb.querySelector('img');
          if (thumbImg) {
            fullSrc = (thumbImg.getAttribute('src') || '')
              .replace(/_120x120/g, '_600x750')
              .replace(/crop_center/g, 'crop_top');
          }
        }
        switchMainImage(fullSrc, thumb);
      });

      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); thumb.click(); }
        /* Arrow key navigation through thumbnail strip */
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          var next = thumbs[idx + 1];
          if (next) { next.focus(); next.click(); }
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          var prev = thumbs[idx - 1];
          if (prev) { prev.focus(); prev.click(); }
        }
      });
    });

    /* ── Gallery main image: prev / next arrow buttons ──────────────────── */
    var arrowPrev = galleryMain ? galleryMain.querySelector('.cl-gallery-arrow--prev') : null;
    var arrowNext = galleryMain ? galleryMain.querySelector('.cl-gallery-arrow--next') : null;

    /* Navigate by triggering the target thumbnail's click — reuses all
       existing switchMainImage / active-thumb / rail-scroll logic. */
    function galleryNavigate(dir) {
      var curIdx = 0;
      thumbs.forEach(function (t, i) {
        if (t.classList.contains('cl-hero__gallery-thumb--active')) curIdx = i;
      });
      var target = (dir === 'next') ? thumbs[curIdx + 1] : thumbs[curIdx - 1];
      if (target) target.click();
    }

    /* Dim / hide arrows at the first / last image */
    function syncArrowState() {
      var curIdx = 0;
      thumbs.forEach(function (t, i) {
        if (t.classList.contains('cl-hero__gallery-thumb--active')) curIdx = i;
      });
      if (arrowPrev) arrowPrev.classList.toggle('cl-gallery-arrow--disabled', curIdx === 0);
      if (arrowNext) arrowNext.classList.toggle('cl-gallery-arrow--disabled', curIdx >= thumbs.length - 1);
    }

    if (arrowPrev) {
      arrowPrev.addEventListener('click', function () {
        galleryNavigate('prev');
        setTimeout(syncArrowState, 120);
      });
    }
    if (arrowNext) {
      arrowNext.addEventListener('click', function () {
        galleryNavigate('next');
        setTimeout(syncArrowState, 120);
      });
    }

    /* Keyboard: left/right when the gallery container has focus */
    if (galleryMain) {
      galleryMain.setAttribute('tabindex', '0');
      galleryMain.setAttribute('aria-label', 'Product image gallery — use arrow keys to navigate');
      galleryMain.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); galleryNavigate('prev'); setTimeout(syncArrowState, 120); }
        if (e.key === 'ArrowRight') { e.preventDefault(); galleryNavigate('next'); setTimeout(syncArrowState, 120); }
      });
    }

    /* Keep arrow state in sync whenever a thumbnail is clicked directly */
    if (thumbsContainer) {
      thumbsContainer.addEventListener('click', function () { setTimeout(syncArrowState, 140); });
    }

    syncArrowState(); /* set initial disabled state */

    /* ── 6. Full-page audit: fix remaining dead buttons/links ──────────── */
    page.querySelectorAll('.cl-btn').forEach(function (btn) {
      if (!btn.getAttribute('tabindex')) btn.setAttribute('tabindex', '0');
      if (btn.tagName === 'A' && !btn.getAttribute('href')) btn.setAttribute('href', '#');
    });
    page.querySelectorAll('.cl-reviews__btn, .cl-reviews__dot, .cl-process__hdr .cl-btn')
      .forEach(function (el) { el.style.cursor = 'pointer'; });

    /* ── 7. Swatch colour fallback for new/unknown colour names ────────── */
    initSwatchColours();

    /* ── 8. Sync colour section with live source product data ───────────
       Fetches the full product JSON (no variant-count limit), determines
       available colours from inventory_quantity / inventory_policy (the
       product JSON for this store omits the "available" boolean field),
       reconciles the DOM, then updates count labels.

       Safety: if zero available colours are found in the API response
       (e.g. temporary outage or unexpected format), we bail out and leave
       the Liquid-rendered swatches untouched rather than wiping them.
       ─────────────────────────────────────────────────────────────────── */
    (function syncColoursWithProduct() {
      /* Determine variant availability from inventory fields.
         The product JSON for this store does not include an "available" boolean;
         availability must be derived from inventory_quantity and inventory_policy. */
      function varIsAvailable(v) {
        if (!v.inventory_management) return true;   /* untracked — always on sale  */
        if (v.inventory_policy === 'continue') return true; /* oversell enabled       */
        return (v.inventory_quantity || 0) > 0;              /* deny policy: need stock */
      }

      fetch('/products/plain-elasticated-hem-top.json')
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var variants = (data.product || {}).variants || [];

          /* Build a set of colour names that have at least one available variant */
          var availableSet = {};
          variants.forEach(function (v) {
            if (varIsAvailable(v) && v.option1) {
              var k = v.option1.toLowerCase();
              if (!availableSet[k]) availableSet[k] = v.option1; /* canonical name */
            }
          });

          /* Safety guard: if the API returned no available colours at all,
             preserve the Liquid-rendered swatches and exit. */
          if (Object.keys(availableSet).length === 0) {
            console.warn('[cl-colours] API returned no available colours — preserving server-rendered swatches.');
            return;
          }

          /* Ordered, deduped list of available colours (follows variant order) */
          var seenKeys = {};
          var orderedColours = [];
          variants.forEach(function (v) {
            var k = (v.option1 || '').toLowerCase();
            if (availableSet[k] && !seenKeys[k]) {
              seenKeys[k] = true;
              orderedColours.push(availableSet[k]);
            }
          });

          var swatchContainer = page.querySelector('.cl-hero__color-swatches');
          var chipsGrid       = page.querySelector('.cl-colors-sec__grid');

          /* Remove swatches for colours no longer in the available set
             (covers colours removed from the source product or going fully OOS) */
          page.querySelectorAll('.cl-hero__color-swatch').forEach(function (sw) {
            var k = (sw.getAttribute('title') || '').toLowerCase();
            if (k && !availableSet[k] && sw.parentNode) sw.parentNode.removeChild(sw);
          });

          /* Remove chips for colours no longer in the available set */
          page.querySelectorAll('.cl-color-chip').forEach(function (chip) {
            var nameEl = chip.querySelector('.cl-color-chip__name');
            var k = nameEl ? nameEl.textContent.trim().toLowerCase() : '';
            if (k && !availableSet[k] && chip.parentNode) chip.parentNode.removeChild(chip);
          });

          /* Build set of currently rendered colour keys (after removals) */
          var renderedKeys = {};
          page.querySelectorAll('.cl-hero__color-swatch').forEach(function (sw) {
            var k = (sw.getAttribute('title') || '').toLowerCase();
            if (k) renderedKeys[k] = true;
          });

          /* Add any available colours not yet rendered
             (covers colours cut off by Liquid's 100-variant window, or newly added) */
          orderedColours.forEach(function (colourName) {
            var k      = colourName.toLowerCase();
            var handle = k.replace(/\s+/g, '-');
            if (renderedKeys[k]) return;

            /* Hero swatch — use <span> to match Liquid-rendered swatches exactly */
            if (swatchContainer) {
              var sw = document.createElement('span');
              sw.className = 'cl-hero__color-swatch cl-swatch--' + handle;
              sw.setAttribute('title', colourName);
              sw.setAttribute('aria-label', colourName);
              sw.setAttribute('data-colour', colourName);
              sw.setAttribute('role', 'button');
              sw.setAttribute('tabindex', '0');
              sw.setAttribute('aria-pressed', 'false');
              sw.style.cursor = 'pointer';
              sw.addEventListener('click', function () {
                selectColour(sw.getAttribute('title') || '', 'swatch');
              });
              sw.addEventListener('keydown', function (e) {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sw.click(); }
              });
              swatchContainer.appendChild(sw);
            }

            /* Colour showcase chip — use <div>/<div> to match Liquid-rendered chips exactly.
               Using <button> as the chip container triggers browser default styles
               (background, border, padding) that break the layout in dark mode / some themes. */
            if (chipsGrid) {
              var chip = document.createElement('div');
              chip.className = 'cl-color-chip';
              chip.setAttribute('role', 'button');
              chip.setAttribute('tabindex', '0');
              chip.setAttribute('aria-pressed', 'false');
              chip.style.cursor = 'pointer';
              chip.innerHTML =
                '<div class="cl-color-chip__swatch cl-swatch--' + handle + '" data-colour="' + colourName + '" aria-hidden="true"></div>' +
                '<span class="cl-color-chip__name">' + colourName + '</span>';
              chip.addEventListener('click', function () {
                var nameEl = chip.querySelector('.cl-color-chip__name');
                var name   = nameEl ? nameEl.textContent.trim() : '';
                selectColour(name, 'chip');
                var heroPanel = page.querySelector('.cl-hero__panel');
                if (heroPanel) heroPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              });
              chip.addEventListener('keydown', function (e) {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); chip.click(); }
              });
              chipsGrid.appendChild(chip);
            }
          });

          /* Apply JS colour backgrounds to any new elements with no CSS match */
          initSwatchColours();

          /* Update count labels with the authoritative API total */
          var total   = orderedColours.length;
          var titleEl = page.querySelector('.cl-colors-sec__title');
          var countEl = page.querySelector('.cl-colors-sec__count');
          if (titleEl) {
            titleEl.textContent = 'Available in ' + total + ' Colour' + (total !== 1 ? 's' : '');
          }
          if (countEl) {
            countEl.textContent = total + ' colourway' + (total !== 1 ? 's' : '') + ' · all in stock';
          }

        })
        .catch(function (err) {
          console.warn('[cl-colours] Could not sync from product JSON:', err.message);
        });
    }());

    /* ═══════════════════════════════════════════════════════════════════════
       7. ADD-TO-CART (AJAX — opens theme cart drawer)
       Source of truth for variant IDs: Shopify's public /products/[handle].js
       endpoint. This endpoint always returns real numeric variant IDs and the
       correct option1/option2 field names, so we never risk sending a title
       or handle string to the cart API.
       The Liquid-embedded #cl-variant-data JSON is used as a synchronous
       fallback if the fetch has not yet resolved when the user clicks.
       ═══════════════════════════════════════════════════════════════════════ */

    var atcBtn       = page.querySelector('#cl-atc-btn');
    var atcErrEl     = document.getElementById('cl-atc-error');
    function clearAtcError() {
      if (atcErrEl) { atcErrEl.classList.remove('is-visible'); atcErrEl.textContent = ''; }
    }
    var clVariants   = [];   /* authoritative list from /products/[handle].js  */
    var atcReady     = false;
    var atcOrigLabel = atcBtn ? (atcBtn.textContent.trim() || 'Request Stock') : 'Request Stock';

    /* ── Seed from embedded Liquid JSON so click works before fetch resolves */
    (function seedFromLiquid() {
      var el = document.getElementById('cl-variant-data');
      if (!el) return;
      try {
        var raw = JSON.parse(el.textContent || '[]');
        /* Liquid JSON uses short keys o1/o2/avail — normalise to Shopify shape */
        clVariants = raw.map(function (v) {
          return {
            id:        v.id,
            option1:   v.o1,
            option2:   v.o2,
            option3:   v.o3,
            available: v.avail,
            title:     (v.o1 || '') + (v.o2 ? ' / ' + v.o2 : '')
          };
        });
      } catch (e) { /* ignore */ }
    }());

    /* ── Fetch authoritative variant data from Shopify product JSON ─────── */
    (function fetchProductJson() {
      if (!atcBtn) return;
      var handle = atcBtn.getAttribute('data-product-handle') || 'plain-elasticated-hem-top';

      fetch('/products/' + handle + '.js')
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (product) {
          /* product.variants is an array from Shopify with numeric .id fields */
          if (Array.isArray(product.variants) && product.variants.length) {
            clVariants = product.variants;
            atcReady   = true;
            console.log(
              '[cl-atc] Product JSON loaded — ' + clVariants.length + ' variants for "' + product.title + '"'
            );
          }
        })
        .catch(function (err) {
          console.warn('[cl-atc] Could not load /products/' + handle + '.js:', err.message,
            '— falling back to Liquid-embedded variant data.');
          atcReady = true; /* let the click proceed with seeded data */
        });
    }());

    /* ── Helpers: read current UI selections ────────────────────────────── */
    function getSelectedColour() {
      var s = page.querySelector('.cl-hero__color-swatch--active');
      return s ? (s.getAttribute('title') || '').trim() : '';
    }

    function getSelectedSize() {
      var s = page.querySelector('.cl-hero__size-btn--active');
      return s ? s.textContent.trim() : '';
    }

    /* ── Variant resolution: colour + size → variant object ─────────────── */
    /*
     * Shopify product JSON shapes each variant as:
     *   { id: 12345678, option1: "Black", option2: "UK 10", available: true, ... }
     *
     * Priority:
     *   1. Exact colour + size match (available)
     *   2. Exact colour + size match (any stock)
     *   3. Colour match (available)
     *   4. Colour match (any stock)
     *   5. Size match (available)
     *   6. First available variant
     */
    function resolveVariant(colour, size) {
      if (!clVariants.length) return null;

      var c = (colour || '').trim().toLowerCase();
      var s = (size   || '').trim().toLowerCase();

      /* 1. Exact colour + size — available */
      for (var i = 0; i < clVariants.length; i++) {
        var v = clVariants[i];
        if (
          (v.option1 || '').toLowerCase() === c &&
          (v.option2 || '').toLowerCase() === s &&
          v.available
        ) return v;
      }

      /* 2. Exact colour + size — any stock */
      for (var i = 0; i < clVariants.length; i++) {
        var v = clVariants[i];
        if (
          (v.option1 || '').toLowerCase() === c &&
          (v.option2 || '').toLowerCase() === s
        ) return v;
      }

      /* 3. Colour match — available */
      for (var i = 0; i < clVariants.length; i++) {
        var v = clVariants[i];
        if ((v.option1 || '').toLowerCase() === c && v.available) return v;
      }

      /* 4. Colour match — any stock */
      for (var i = 0; i < clVariants.length; i++) {
        if ((clVariants[i].option1 || '').toLowerCase() === c) return clVariants[i];
      }

      /* 5. Size match — available */
      for (var i = 0; i < clVariants.length; i++) {
        var v = clVariants[i];
        if ((v.option2 || '').toLowerCase() === s && v.available) return v;
      }

      /* 6. First available */
      for (var i = 0; i < clVariants.length; i++) {
        if (clVariants[i].available) return clVariants[i];
      }

      return null; /* no available variant — caller will alert the user */
    }

    /* ── Button state machine ────────────────────────────────────────────── */
    function atcSetState(state, message) {
      if (!atcBtn) return;
      atcBtn.classList.remove('cl-btn--loading', 'cl-btn--error', 'cl-btn--success');
      /* Always clear the error element on every state change */
      if (atcErrEl) { atcErrEl.classList.remove('is-visible'); atcErrEl.textContent = ''; }

      if (state === 'loading') {
        atcBtn.disabled    = true;
        atcBtn.classList.add('cl-btn--loading');
        atcBtn.textContent = 'Adding…';

      } else if (state === 'success') {
        atcBtn.disabled    = false;
        atcBtn.classList.add('cl-btn--success');
        atcBtn.textContent = '✓ Added to Cart';
        setTimeout(function () {
          atcBtn.classList.remove('cl-btn--success');
          atcBtn.textContent = atcOrigLabel;
        }, 2500);

      } else if (state === 'error') {
        /* Button stays intact — full message shown in the dedicated error element */
        atcBtn.disabled    = false;
        atcBtn.textContent = atcOrigLabel;
        if (atcErrEl) {
          atcErrEl.textContent = message || 'Could not add to cart — please try again.';
          atcErrEl.classList.add('is-visible');
        }
        setTimeout(function () {
          if (atcErrEl) { atcErrEl.classList.remove('is-visible'); atcErrEl.textContent = ''; }
        }, 7000);

      } else { /* 'ready' */
        atcBtn.disabled    = false;
        atcBtn.textContent = atcOrigLabel;
      }
    }

    /* ── Open the theme's existing cart drawer ───────────────────────────── *
     * Strategy: scan the DOM once at init for a safe (non-navigation) trigger.
     * "Safe" means the element will NOT cause page navigation when clicked —
     * i.e. it is never an <a> tag or any element with an href attribute.
     * Anchor clicks were the root cause of the blank "1" page: clicking
     * <a href="/cart"> programmatically navigates the browser even when the
     * theme's jQuery handler normally calls e.preventDefault(), because
     * programmatic .click() bypasses jQuery's delegated handlers in some setups.
     * ─────────────────────────────────────────────────────────────────────── */
    var openThemeCart = (function () {

      /* Returns true only for button/div/span elements that cannot navigate */
      function isSafe(el) {
        if (!el) return false;
        var tag = el.tagName;
        if (tag === 'A' || tag === 'AREA') return false;   /* anchor → navigates  */
        if (el.getAttribute('href'))        return false;   /* has href → navigates */
        if (tag === 'FORM')                 return false;   /* form submit          */
        if (el.type === 'submit')           return false;   /* submit button        */
        return true;
      }

      /* Data-attribute / ID selectors that are explicitly JS-only triggers.
         No anchor-link selectors — those cause navigation. */
      var ATTR_SELECTORS = [
        '[data-cart-toggle]',
        '[data-open-cart]',
        '[data-cart-open]',
        '[data-action="cart:open"]',
        '[aria-controls="cart-drawer"]',
        '[aria-controls="CartDrawer"]',
        '.js-cart-toggle',
        '.js-toggle-cart',
        '#CartToggle',
        '#cart-toggle'
      ];

      /* ── DOM-walk fallback: find the cart-count badge, then walk up the
            tree to find its nearest safe clickable ancestor (button / role=button).
            The count badge is almost always nested inside the cart-icon button,
            so this covers themes that use no data-attributes at all. ── */
      function findTriggerViaCountBadge() {
        var COUNT_SELECTORS = [
          '[data-cart-count]', '.cart-count', '.cart__count',
          '#cart-count', '#CartCount', '.cart-item-count'
        ];
        for (var ci = 0; ci < COUNT_SELECTORS.length; ci++) {
          var badge = document.querySelector(COUNT_SELECTORS[ci]);
          if (!badge) continue;
          var node = badge.parentElement;
          while (node && node !== document.body) {
            if (isSafe(node) &&
                (node.tagName === 'BUTTON' ||
                 node.getAttribute('role') === 'button' ||
                 node.getAttribute('data-cart-toggle') !== null ||
                 node.getAttribute('data-open-cart')   !== null)) {
              return node;
            }
            node = node.parentElement;
          }
        }
        return null;
      }

      /* Scan once at script-parse time */
      var _trigger = null;
      for (var _i = 0; _i < ATTR_SELECTORS.length; _i++) {
        var _el = document.querySelector(ATTR_SELECTORS[_i]);
        if (isSafe(_el)) { _trigger = _el; break; }
      }
      if (!_trigger) _trigger = findTriggerViaCountBadge();

      console.log('[cl-cart] trigger found at init:',
        _trigger ? (_trigger.tagName + ' #' + _trigger.id + ' .' + _trigger.className) : 'none');

      return function openThemeCart() {

        /* 1. Refresh every cart-count badge via /cart.js (background, no navigation) */
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var n = String(cart.item_count || 0);
            document.querySelectorAll(
              '[data-cart-count], .cart-count, .cart__count,' +
              '#cart-count, #CartCount, .cart-item-count, .header__cart-count'
            ).forEach(function (el) {
              if (el.tagName !== 'INPUT') el.textContent = n;
            });
          })
          .catch(function () {});

        /* 2. Click the cached safe trigger */
        if (_trigger && isSafe(_trigger)) { _trigger.click(); return; }

        /* 3. Re-scan (handles themes that render the header asynchronously) */
        for (var i = 0; i < ATTR_SELECTORS.length; i++) {
          var el = document.querySelector(ATTR_SELECTORS[i]);
          if (isSafe(el)) { el.click(); return; }
        }
        var walked = findTriggerViaCountBadge();
        if (walked) { walked.click(); return; }

        /* 4. Global JS objects exposed by some premium themes (safe, no navigation) */
        if (window.CartDrawer && typeof window.CartDrawer.open === 'function') {
          window.CartDrawer.open(); return;
        }
        if (window.theme && window.theme.cart &&
            typeof window.theme.cart.open === 'function') {
          window.theme.cart.open(); return;
        }

        /* 5. No drawer found — cart count was already refreshed above, which
              is the minimum correct behaviour. No redirect. */
        console.warn('[cl-cart] Could not locate a safe cart-drawer trigger. ' +
          'Open browser DevTools, run: document.querySelector(\'[data-cart-toggle]\') ' +
          'and share the result to wire up the correct selector.');
      };
    }());

    /* ── Click handler ───────────────────────────────────────────────────── */
    if (atcBtn) {
      atcBtn.addEventListener('click', function (e) {
        e.preventDefault();

        var colour  = getSelectedColour();
        var size    = getSelectedSize();
        /* qty = (units from stepper) × (units per pack: 1 or 3) */
        var qty     = getQty() * getPackQty();
        var variant = resolveVariant(colour, size);

        /* Guard: must have a real, available variant with a numeric ID */
        if (!variant || !variant.id || !variant.available) {
          atcSetState('error', 'This variant is currently out of stock.');
          return;
        }

        /* Requirement: always cast to Number so the API receives an integer */
        var selectedVariantId = Number(variant.id);

        /* Requirement: console log the selected variant before cart add */
        console.log('[cl-atc] Selected variant:', {
          id:        selectedVariantId,
          title:     variant.title,
          option1:   variant.option1,
          option2:   variant.option2,
          option3:   variant.option3  || null,
          price:     variant.price    || null,
          available: variant.available,
          quantity:  qty
        });

        atcSetState('loading');

        /* Requirement: use items[] array format */
        fetch('/cart/add.js', {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept':       'application/json'
          },
          body: JSON.stringify({
            items: [{
              id:       selectedVariantId,
              quantity: qty
            }]
          })
        })
        .then(function (res) {
          if (!res.ok) {
            /* Parse Shopify's error body for a human-readable message */
            return res.json().then(function (body) {
              throw new Error(
                body.description || body.message || ('HTTP ' + res.status)
              );
            });
          }
          return res.json();
        })
        .then(function () {
          atcSetState('success');
          openThemeCart();
        })
        .catch(function (err) {
          console.error('[cl-atc] /cart/add.js failed:', err.message);
          /* Surface a brief, readable message in the button */
          atcSetState('error', err.message || 'Could not add to cart — please try again.');
        });
      });
    }
  }

  /* ─── Hero entrance ───────────────────────────────────────────────────── */
  function initHero() {
    var tl = gsap.timeline({ delay: 0.1, defaults: { ease: 'power3.out' } });

    tl.to('.cl-hero__tag',      { opacity: 1, y: 0, duration: 0.6 })
      .to('.cl-hero__headline', { opacity: 1, y: 0, duration: 0.85 }, '-=0.45')
      .to('.cl-hero__sub',      { opacity: 1, y: 0, duration: 0.7  }, '-=0.5')
      .to('.cl-hero__stats',    { opacity: 1, y: 0, duration: 0.65 }, '-=0.45')
      .to('.cl-hero__cta',      { opacity: 1, y: 0, duration: 0.6  }, '-=0.4')
      .to('.cl-hero__mosaic-item',
        { opacity: 1, scale: 1, duration: 0.7, stagger: 0.1, ease: 'back.out(1.3)' },
        '-=0.3'
      );

    /* Trigger CSS image-scale transition via is-loaded class */
    requestAnimationFrame(function () {
      var hero = document.querySelector('.cl-hero');
      if (hero) hero.classList.add('is-loaded');
    });
  }

  /* ─── Generic scroll reveal ([data-cl-reveal]) ────────────────────────── */
  function initScrollReveal() {
    document.querySelectorAll('[data-cl-reveal]').forEach(function (el) {
      /* Skip elements handled by their own dedicated stagger functions */
      if (el.closest('.cl-viral') || el.closest('.cl-pcard')) return;

      gsap.fromTo(
        el,
        { opacity: 0, y: 20 },
        {
          opacity: 1, y: 0, duration: 0.7, ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
        }
      );
    });
  }

  /* ─── Trust bar ───────────────────────────────────────────────────────── */
  function initTrustReveal() {
    if (!document.querySelector('.cl-trust__item')) return;

    gsap.fromTo(
      '.cl-trust__item',
      { opacity: 0, y: 16 },
      {
        opacity: 1, y: 0, duration: 0.6, stagger: 0.07, ease: 'power3.out',
        scrollTrigger: {
          trigger: '.cl-trust',
          start: 'top 88%',
          toggleActions: 'play none none none',
        },
      }
    );
  }

  /* ─── Product card grids (all .cl-pgrid) ─────────────────────────────── */
  function initCardStagger() {
    document.querySelectorAll('.cl-pgrid').forEach(function (grid) {
      var cards = grid.querySelectorAll('.cl-pcard');
      if (!cards.length) return;

      gsap.fromTo(
        cards,
        { opacity: 0, y: 28 },
        {
          opacity: 1, y: 0, duration: 0.55, stagger: 0.06, ease: 'power3.out',
          scrollTrigger: {
            trigger: grid,
            start: 'top 84%',
            toggleActions: 'play none none none',
          },
        }
      );
    });
  }

  /* ─── Viral / trending cards ──────────────────────────────────────────── */
  function initViralCards() {
    var viral = document.querySelector('.cl-viral .cl-pgrid');
    if (!viral) return;

    gsap.fromTo(
      viral.querySelectorAll('.cl-pcard'),
      { opacity: 0, y: 24 },
      {
        opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: {
          trigger: viral,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );
  }

  /* ─── Features section ────────────────────────────────────────────────── */
  function initFeatureReveal() {
    var features = document.querySelectorAll('.cl-feature');
    if (!features.length) return;

    gsap.fromTo(
      features,
      { opacity: 0, y: 20 },
      {
        opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: {
          trigger: '.cl-features',
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      }
    );
  }

  /* ─── Process / how-it-works steps ───────────────────────────────────── */
  function initProcessReveal() {
    var steps = document.querySelectorAll('.cl-process__step');
    if (!steps.length) return;

    gsap.fromTo(
      steps,
      { opacity: 0, y: 24 },
      {
        opacity: 1, y: 0, duration: 0.65, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: {
          trigger: '.cl-process__steps',
          start: 'top 82%',
          toggleActions: 'play none none none',
        },
      }
    );
  }

  /* ─── Reviews carousel ────────────────────────────────────────────────── */
  function initReviewsCarousel() {
    var track    = document.getElementById('cl-reviews-track');
    var prevBtn  = document.getElementById('cl-reviews-prev');
    var nextBtn  = document.getElementById('cl-reviews-next');
    var dotsWrap = document.getElementById('cl-reviews-dots');

    if (!track) return;

    var cards       = Array.from(track.querySelectorAll('.cl-rcard'));
    var GAP         = 20;
    var currentIdx  = 0;
    var isAnimating = false;

    if (!cards.length) return;

    /* Entrance animation */
    gsap.fromTo(
      cards,
      { opacity: 0, y: 20 },
      {
        opacity: 1, y: 0, duration: 0.6, stagger: 0.08, ease: 'power3.out',
        scrollTrigger: {
          trigger: '.cl-reviews',
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    /* Number of fully visible cards at current viewport width */
    function visibleCount() {
      var trackW = track.parentElement ? track.parentElement.offsetWidth : window.innerWidth;
      var cardW  = cards[0].offsetWidth + GAP;
      return Math.max(1, Math.floor((trackW + GAP) / cardW));
    }

    function maxIndex() {
      return Math.max(0, cards.length - visibleCount());
    }

    /* Build dot indicators based on current maxIndex */
    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      var total = maxIndex() + 1;
      for (var i = 0; i < total; i++) {
        var dot = document.createElement('button');
        dot.className = 'cl-reviews__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.dataset.idx = i;
        dotsWrap.appendChild(dot);
      }
    }

    function updateDots() {
      if (!dotsWrap) return;
      Array.from(dotsWrap.querySelectorAll('.cl-reviews__dot')).forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === currentIdx);
      });
    }

    function goTo(idx) {
      if (isAnimating) return;
      currentIdx  = Math.min(Math.max(idx, 0), maxIndex());
      isAnimating = true;

      gsap.to(track, {
        x: -(currentIdx * (cards[0].offsetWidth + GAP)),
        duration: 0.45,
        ease: 'power3.inOut',
        onComplete: function () { isAnimating = false; },
      });

      updateDots();
    }

    /* Prev / Next buttons */
    if (prevBtn) {
      prevBtn.addEventListener('click', function () { goTo(currentIdx - 1); });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () { goTo(currentIdx + 1); });
    }

    /* Dot clicks (event delegation) */
    if (dotsWrap) {
      dotsWrap.addEventListener('click', function (e) {
        var dot = e.target.closest('.cl-reviews__dot');
        if (dot) goTo(parseInt(dot.dataset.idx, 10));
      });
    }

    /* Touch / swipe */
    var touchStartX = 0;
    track.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    track.addEventListener('touchend', function (e) {
      var dx = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(dx) > 48) {
        goTo(dx > 0 ? currentIdx + 1 : currentIdx - 1);
      }
    }, { passive: true });

    /* Resize: rebuild dots and reset to first slide */
    window.addEventListener('resize', function () {
      buildDots();
      goTo(0);
    });

    /* Initialise */
    buildDots();
    updateDots();
  }

  /* ═══════════════════════════════════════════════════════════════════════
     TOP SELLING PRODUCTS CAROUSEL — §9.5
     Infinite-loop, auto-playing, touch-enabled product carousel.

     Technique: "3-copy" infinite loop
     ──────────────────────────────────
     The track holds three full sets of product cards:
       [clone set | original set | clone set]
     The track starts positioned at the first real card (index n).
     When the track advances past index 2n (entering the 2nd clone set),
     the transitionend handler silently snaps back to the equivalent
     position in the original set — the viewer never sees a jump because
     the clone looks identical to the real card.
     The reverse applies for backwards navigation.

     Card widths are set by JS so the correct number fills the viewport
     at every breakpoint (4 / 3 / 2 cards per view).
     ═══════════════════════════════════════════════════════════════════════ */
  function initTspCarousel() {
    var section  = document.getElementById('cl-tsp');
    if (!section) return;

    var viewport = document.getElementById('cl-tsp-viewport');
    var track    = document.getElementById('cl-tsp-track');
    var prevBtn  = document.getElementById('cl-tsp-prev');
    var nextBtn  = document.getElementById('cl-tsp-next');
    var dotsWrap = document.getElementById('cl-tsp-dots');

    if (!track || !viewport) return;

    /* Collect original (non-clone) cards only */
    var origCards = Array.from(track.querySelectorAll('.cl-tsp__card:not(.cl-tsp__card--clone)'));
    var n         = origCards.length;
    if (n < 2) return;

    var GAP   = 20;    /* px — must match CSS gap on .cl-tsp__track  */
    var SPEED = 480;   /* transition duration in ms                   */
    var DELAY = 3500;  /* autoplay interval in ms                     */
    var cpv   = 4;     /* cards per view — recalculated on each draw  */
    var cur   = n;     /* current track-index (n = first real card)   */
    var anim  = false; /* true while a CSS transition is in flight    */
    var timer = null;

    /* ── Responsive: cards per view ─────────────────────────────────── */
    function getCpv() {
      var w = window.innerWidth;
      return w >= 1024 ? 4 : w >= 768 ? 2 : 1;
    }

    /* ── Card width: fills viewport exactly with cpv cards + gaps ───── */
    function getCardW() {
      return (viewport.offsetWidth - GAP * (cpv - 1)) / cpv;
    }

    /* ── Apply width to every card currently in the track ───────────── */
    function applyCardWidths() {
      var w = getCardW();
      Array.from(track.querySelectorAll('.cl-tsp__card')).forEach(function(c) {
        c.style.width = w + 'px';
      });
    }

    /* ── translateX for a given track-index ─────────────────────────── */
    function offsetFor(idx) {
      return -(idx * (getCardW() + GAP));
    }

    /* ── Move track to position idx (animated or instant) ───────────── */
    function setPos(idx, animated) {
      track.style.transition = animated
        ? ('transform ' + SPEED + 'ms cubic-bezier(0.4, 0, 0.2, 1)')
        : 'none';
      track.style.transform = 'translateX(' + offsetFor(idx) + 'px)';
    }

    /* ── Logical index (0 … n-1), handles clone-set overflow ────────── */
    function logIdx() {
      return ((cur - n) % n + n) % n;
    }

    /* ── Clone cards: build [clones | originals | clones] ───────────── */
    function buildClones() {
      /* Remove stale clones from a previous build */
      Array.from(track.querySelectorAll('.cl-tsp__card--clone')).forEach(function(c) {
        c.remove();
      });

      /* Prepend a full copy of the originals */
      var before = document.createDocumentFragment();
      origCards.forEach(function(c) {
        var cl = c.cloneNode(true);
        cl.classList.add('cl-tsp__card--clone');
        /* Strip any GSAP-applied inline styles. cloneNode(true) copies the
           element's current inline style — if GSAP has set opacity:0 or
           transform:translateY(24px) on the original, the clone inherits it
           and becomes permanently invisible. Clearing these properties resets
           the clone to its CSS-defined defaults (opacity:1, transform:none). */
        cl.style.opacity    = '';
        cl.style.transform  = '';
        cl.style.visibility = '';
        before.appendChild(cl);
      });
      track.insertBefore(before, track.firstChild);

      /* Append a full copy of the originals */
      origCards.forEach(function(c) {
        var cl = c.cloneNode(true);
        cl.classList.add('cl-tsp__card--clone');
        cl.style.opacity    = '';   /* same guard as above */
        cl.style.transform  = '';
        cl.style.visibility = '';
        track.appendChild(cl);
      });
    }

    /* ── Rebuild on resize (debounced 160ms) ─────────────────────────── */
    var resizeDebounce;
    window.addEventListener('resize', function() {
      clearTimeout(resizeDebounce);
      resizeDebounce = setTimeout(function() {
        var li = logIdx();
        cpv    = getCpv();
        buildClones();
        applyCardWidths();
        cur = n + li;
        setPos(cur, false);
        buildDots();
        updateDots();
      }, 160);
    });

    /* ── Dot indicators ──────────────────────────────────────────────── */
    var dotEls = [];

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      dotEls = [];
      for (var i = 0; i < n; i++) {
        var d = document.createElement('button');
        d.type      = 'button';
        d.className = 'cl-tsp__dot';
        d.setAttribute('role', 'tab');
        d.setAttribute('aria-label', 'Product ' + (i + 1));
        d.dataset.i = i;
        dotsWrap.appendChild(d);
        dotEls.push(d);
      }
    }

    function updateDots() {
      var li = logIdx();
      dotEls.forEach(function(d, i) {
        d.classList.toggle('is-active', i === li);
        d.setAttribute('aria-selected', i === li ? 'true' : 'false');
      });
    }

    /* ── Step carousel by delta (+1 / -1) ───────────────────────────── */
    function step(delta) {
      if (anim) return;
      anim = true;
      cur += delta;
      setPos(cur, true);
      updateDots();
    }

    /* Go directly to a logical index */
    function goTo(li) {
      if (anim) return;
      anim = true;
      cur  = n + li;
      setPos(cur, true);
      updateDots();
    }

    /* ── transitionend: snap back from clone zone ────────────────────── */
    track.addEventListener('transitionend', function(e) {
      if (e.propertyName !== 'transform') return;
      /* Snap to real position if we've drifted into a clone set */
      if (cur >= 2 * n) {
        cur -= n;
        setPos(cur, false);
      } else if (cur < n) {
        cur += n;
        setPos(cur, false);
      }
      updateDots();
      anim = false; /* reset AFTER potential no-animation snap */
    });

    /* ── Autoplay ─────────────────────────────────────────────────────── */
    function startAuto() {
      stopAuto();
      timer = setInterval(function() { step(1); }, DELAY);
    }

    function stopAuto() {
      clearInterval(timer);
      timer = null;
    }

    /* ── Pause on hover (desktop only) ──────────────────────────────── */
    section.addEventListener('mouseenter', stopAuto);
    section.addEventListener('mouseleave', function() {
      if (document.hasFocus()) startAuto();
    });

    /* ── Arrow buttons ───────────────────────────────────────────────── */
    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        stopAuto(); step(-1); startAuto();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        stopAuto(); step(1); startAuto();
      });
    }

    /* ── Dot navigation (event delegation) ──────────────────────────── */
    if (dotsWrap) {
      dotsWrap.addEventListener('click', function(e) {
        var d = e.target.closest('.cl-tsp__dot');
        if (d) { stopAuto(); goTo(parseInt(d.dataset.i, 10)); startAuto(); }
      });
    }

    /* ── Touch / swipe ───────────────────────────────────────────────── */
    var touchX = 0;
    track.addEventListener('touchstart', function(e) {
      touchX = e.touches[0].clientX;
      stopAuto();
    }, { passive: true });

    track.addEventListener('touchend', function(e) {
      var dx = touchX - e.changedTouches[0].clientX;
      if (Math.abs(dx) > 44) { step(dx > 0 ? 1 : -1); }
      startAuto();
    }, { passive: true });

    /* ── Initialise ─────────────────────────────────────────────────────
       IMPORTANT: buildClones() MUST run before gsap.fromTo().
       gsap.fromTo() immediately sets opacity:0 as an inline style on every
       element in origCards. cloneNode(true) copies inline styles, so any
       clone created after gsap.fromTo() would inherit opacity:0 and stay
       permanently invisible — the exact cause of blank slides.
       Building clones first means they are copied from clean DOM elements,
       before GSAP has written anything to the originals.
       ──────────────────────────────────────────────────────────────────── */
    cpv = getCpv();
    buildClones();       /* ← must be first, before GSAP touches origCards */
    applyCardWidths();
    buildDots();
    cur = n;             /* start at first real card */
    setPos(cur, false);
    updateDots();
    startAuto();

    /* ── GSAP entrance animation (runs AFTER clones are built) ───────── */
    gsap.fromTo(
      origCards,         /* targets original cards only — clones are unaffected */
      { opacity: 0, y: 24 },
      {
        opacity: 1, y: 0, duration: 0.55, stagger: 0.06, ease: 'power3.out',
        scrollTrigger: {
          trigger: '#cl-tsp',
          start: 'top 82%',
          toggleActions: 'play none none none',
        },
      }
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SEO CONTENT SECTION — Read More toggle + FAQ accordion
     ───────────────────────────────────────────────────────────────────────
     Read More logic
     ───────────────
     The content div has CSS max-height: 230px in its collapsed state.
     On expand we measure the real scrollHeight and set that as max-height so
     the CSS transition runs over the exact distance, then we remove max-height
     after the transition completes so the content can reflow freely.
     On collapse we reverse the sequence: set scrollHeight (from 'none'),
     force a repaint, then snap to 230px so the CSS transition can animate.

     FAQ accordion logic
     ────────────────────
     Uses the CSS grid-template-rows: 0fr → 1fr trick.  No JS height
     measurement needed.  Only one FAQ can be open at a time.
     ═══════════════════════════════════════════════════════════════════════ */
  function initSeoSection() {
    /* ── Read More ────────────────────────────────────────────────────── */
    var toggleBtn   = document.getElementById('cl-seo-toggle');
    var toggleLabel = document.getElementById('cl-seo-toggle-label');
    var content     = document.getElementById('cl-seo-content');
    var fade        = document.getElementById('cl-seo-fade');
    var section     = document.getElementById('cl-seo-section');

    if (toggleBtn && content) {
      var expanded = false;

      toggleBtn.addEventListener('click', function () {
        expanded = !expanded;

        if (expanded) {
          /* ── Expand ──────────────────────────────────────────── */
          /* Animate to real content height */
          content.style.maxHeight = content.scrollHeight + 'px';

          /* After the CSS transition finishes, remove the fixed max-height
             so the content can reflow freely (e.g. on window resize). */
          content.addEventListener('transitionend', function onExpanded(e) {
            if (e.propertyName !== 'max-height') return;
            content.style.maxHeight = 'none';
            content.removeEventListener('transitionend', onExpanded);
          });

          if (fade) fade.classList.add('is-gone');
          toggleLabel.textContent = 'Show Less';
          toggleBtn.setAttribute('aria-expanded', 'true');

        } else {
          /* ── Collapse ────────────────────────────────────────── */
          /* If max-height was 'none' the transition won't fire — we need
             to set a concrete pixel value first, then in the next frame
             switch to the collapsed height so the transition kicks in. */
          content.style.maxHeight = content.scrollHeight + 'px';

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              content.style.maxHeight = '230px';
            });
          });

          if (fade) fade.classList.remove('is-gone');
          toggleLabel.textContent = 'Read More';
          toggleBtn.setAttribute('aria-expanded', 'false');

          /* Scroll the section back into view so the user isn't left
             staring at the bottom of collapsed content. */
          if (section) {
            setTimeout(function () {
              section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 320); /* wait for collapse transition (0.55s) to start */
          }
        }
      });
    }

    /* ── FAQ Accordion ────────────────────────────────────────────────── */
    var faqItems = Array.from(
      document.querySelectorAll('.cl-seo__faq-item')
    );

    /* Open a single FAQ item — updates aria, answer grid, and item class */
    function openFaq(item) {
      var btn    = item.querySelector('.cl-seo__faq-btn');
      var answer = item.querySelector('.cl-seo__faq-answer');
      if (btn)    btn.setAttribute('aria-expanded', 'true');
      if (answer) answer.classList.add('is-open');
      item.classList.add('is-open');
    }

    /* Close a single FAQ item */
    function closeFaq(item) {
      var btn    = item.querySelector('.cl-seo__faq-btn');
      var answer = item.querySelector('.cl-seo__faq-answer');
      if (btn)    btn.setAttribute('aria-expanded', 'false');
      if (answer) answer.classList.remove('is-open');
      item.classList.remove('is-open');
    }

    faqItems.forEach(function (item) {
      var btn = item.querySelector('.cl-seo__faq-btn');
      if (!btn) return;

      btn.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');

        /* Close every other item first */
        faqItems.forEach(function (other) {
          if (other !== item) closeFaq(other);
        });

        /* Toggle the clicked item */
        if (isOpen) { closeFaq(item); } else { openFaq(item); }
      });

      /* Arrow keys navigate between questions (ARIA pattern) */
      btn.addEventListener('keydown', function (e) {
        var idx = faqItems.indexOf(item);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          var next = faqItems[idx + 1];
          if (next) next.querySelector('.cl-seo__faq-btn').focus();
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          var prev = faqItems[idx - 1];
          if (prev) prev.querySelector('.cl-seo__faq-btn').focus();
        }
        if (e.key === 'Home') {
          e.preventDefault();
          faqItems[0].querySelector('.cl-seo__faq-btn').focus();
        }
        if (e.key === 'End') {
          e.preventDefault();
          faqItems[faqItems.length - 1].querySelector('.cl-seo__faq-btn').focus();
        }
      });
    });

    /* Open the first FAQ by default on page load */
    if (faqItems.length > 0) { openFaq(faqItems[0]); }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     COUNTDOWN TIMER
     ───────────────────────────────────────────────────────────────────────
     Session-only 45-minute countdown.  No localStorage, no cookies.
     Every page refresh restarts from 45:00.

     Behaviour:
       • Ticks once per second via setInterval.
       • Under 5 minutes (300 s): adds .is-urgent to the value span for a
         pulse animation.
       • At 0: clears the interval, hides the countdown bar, shows the
         "Offer Expired" state.

     Guard: dataset.started prevents double-init if the DOMContentLoaded
     callback and the readyState fallback both fire.
     ═══════════════════════════════════════════════════════════════════════ */
  function initCountdownTimer() {
    var valEl     = document.getElementById('cl-countdown-val');
    var barEl     = document.getElementById('cl-hero-countdown');
    var expiredEl = document.getElementById('cl-countdown-expired');

    if (!valEl || !barEl) return;

    /* Guard: prevent double-init on pages where DOMContentLoaded + the
       readyState fallback both execute (e.g. deferred script injection) */
    if (valEl.dataset.started) return;
    valEl.dataset.started = '1';

    var TOTAL   = 45 * 60;   /* 2700 seconds = 45 minutes */
    var URGENT  = 5  * 60;   /* 300 seconds = 5 minutes — start pulsing */
    var remaining = TOTAL;

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function render() {
      var m = Math.floor(remaining / 60);
      var s = remaining % 60;
      valEl.textContent = pad(m) + ':' + pad(s);

      /* Add urgency pulse in the final 5 minutes */
      if (remaining <= URGENT) {
        valEl.classList.add('is-urgent');
      }
    }

    var interval = setInterval(function () {
      remaining -= 1;

      if (remaining <= 0) {
        clearInterval(interval);
        /* Hide countdown bar */
        barEl.style.display = 'none';
        /* Show expired notice */
        if (expiredEl) expiredEl.style.display = 'flex';
        return;
      }

      render();
    }, 1000);

    /* Render initial state immediately so the display is correct before
       the first 1-second tick fires */
    render();
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SWATCH COLOUR FALLBACK
     ───────────────────────────────────────────────────────────────────────
     CSS cl-swatch--{handleize} classes cover all known colour names.
     For any NEW colour added to the Shopify product that has no matching
     CSS rule, this function derives a plausible HSL colour from the name
     using keyword heuristics — ensuring no blank/white placeholder appears.
     ═══════════════════════════════════════════════════════════════════════ */
  function nameToColour(name) {
    var n = (name || '').toLowerCase().trim();

    /* 1. Explicit dictionary — always checked first; guarantees correct colours
          for all known product colour names regardless of CSS class coverage. */
    var COLOUR_MAP = {
      /* Neutrals */
      'black':          '#1a1a1a', 'white':         '#ffffff',
      'grey':           '#808080', 'gray':          '#808080',
      'light grey':     '#d3d3d3', 'light gray':    '#d3d3d3',
      'dark grey':      '#555555', 'dark gray':     '#555555',
      'charcoal':       '#36454f', 'silver':        '#c0c0c0',
      /* Warm neutrals */
      'beige':          '#f5f5dc', 'cream':         '#f5f0e8', 'ivory':        '#fffff0',
      'nude':           '#e8c9a0', 'ecru':          '#c2b280', 'champagne':    '#f7e7ce',
      'stone':          '#d2c6b2', 'sand':          '#c2b280', 'camel':        '#c19a6b',
      'tan':            '#d2b48c', 'khaki':         '#bdb76b', 'dark khaki':   '#8b8458',
      'mocha':          '#8b6f61', 'brown':         '#8b4513', 'chocolate':    '#4a2c17',
      'olive':          '#556b2f', 'mustard':       '#d4a017', 'gold':         '#d4af37',
      /* Yellows & Oranges */
      'yellow':         '#fbbf24', 'lemon':         '#fff44f',
      'orange':         '#f97316', 'coral':         '#ff5c8a',
      'salmon':         '#fa8072', 'peach':         '#ffcba4', 'apricot':      '#fbceb1',
      'terracotta':     '#e2725b', 'rust':          '#b7410e',
      /* Reds */
      'red':            '#dc2626', 'dark red':      '#8b0000',
      'burgundy':       '#800020', 'wine':          '#722f37', 'maroon':       '#800000',
      'raspberry':      '#e30b5d',
      /* Pinks */
      'pink':           '#ff69b4', 'baby pink':     '#f4c2c2',
      'light pink':     '#f4c2c2', 'pale pink':     '#f4c2c2',
      'hot pink':       '#ff1493', 'fuchsia':       '#d63384', 'magenta':      '#cc00cc',
      'blush':          '#f4a7b9', 'rose':          '#ff007f', 'watermelon':   '#fc6c85',
      /* Purples */
      'purple':         '#8b5cf6', 'violet':        '#7f00ff',
      'plum':           '#673147', 'mauve':         '#e0b0c0',
      'lavender':       '#e6d7f7', 'lilac':         '#d8b4fe', 'aubergine':    '#3d0734',
      /* Greens */
      'green':          '#22c55e', 'mint':          '#98ff98', 'mint green':   '#98ff98',
      'sage':           '#9caf88', 'lime':          '#bfff00', 'lime green':   '#bfff00',
      'dark green':     '#166534', 'forest green':  '#228b22', 'bottle green': '#006400',
      'hunter green':   '#355e3b', 'emerald':       '#50c878',
      /* Blues & Teals */
      'turquoise':      '#40e0d0', 'teal':          '#008080', 'aqua':         '#00ffff',
      'petrol':         '#005f6a', 'duck egg':      '#96ded1',
      'sky blue':       '#87ceeb', 'sky':           '#87ceeb', 'powder blue':  '#b0e0e6',
      'baby blue':      '#89cff0', 'blue':          '#3b82f6',
      'denim':          '#1560bd', 'denim blue':    '#1560bd',
      'royal blue':     '#4169e1', 'cobalt':        '#0047ab', 'cobalt blue':  '#0047ab',
      'navy':           '#001f5b', 'navy blue':     '#001f5b',
      'dark blue':      '#00008b', 'midnight blue': '#191970',
    };
    if (Object.prototype.hasOwnProperty.call(COLOUR_MAP, n)) return COLOUR_MAP[n];

    /* 2. Try the name as a CSS named colour (works for "coral", "teal", "salmon", etc.) */
    if (typeof CSS !== 'undefined' && CSS.supports) {
      if (CSS.supports('color', n)) return n;
      /* Also try without spaces — "skyblue", "royalblue" etc. are valid CSS */
      var slug = n.replace(/\s+/g, '');
      if (CSS.supports('color', slug)) return slug;
    }

    /* 3. Keyword heuristics — pick hue/saturation/lightness from colour family */
    var h, s = 60, l = 50;
    if      (n.indexOf('white') !== -1 || n.indexOf('ivory') !== -1 || n.indexOf('ecru') !== -1)                              { h = 45;  s = 20; l = 96; }
    else if (n.indexOf('cream') !== -1 || n.indexOf('champagne') !== -1)                                                      { h = 40;  s = 35; l = 90; }
    else if (n.indexOf('beige') !== -1 || n.indexOf('nude') !== -1 || n.indexOf('sand') !== -1 || n.indexOf('stone') !== -1)  { h = 38;  s = 30; l = 80; }
    else if (n.indexOf('camel') !== -1 || n.indexOf('tan') !== -1)                                                            { h = 35;  s = 50; l = 60; }
    else if (n.indexOf('mocha') !== -1)                                                                                        { h = 15;  s = 25; l = 46; }
    else if (n.indexOf('khaki') !== -1 || n.indexOf('olive') !== -1)                                                          { h = 55;  s = 35; l = 50; }
    else if (n.indexOf('mustard') !== -1 || n.indexOf('gold') !== -1)                                                         { h = 45;  s = 70; l = 50; }
    else if (n.indexOf('yellow') !== -1 || n.indexOf('lemon') !== -1)                                                         { h = 55;  s = 90; l = 60; }
    else if (n.indexOf('orange') !== -1)                                                                                       { h = 25;  s = 90; l = 55; }
    else if (n.indexOf('coral') !== -1 || n.indexOf('salmon') !== -1)                                                         { h = 16;  s = 100; l = 66; }
    else if (n.indexOf('burgundy') !== -1 || n.indexOf('wine') !== -1 || n.indexOf('maroon') !== -1)                          { h = 345; s = 70; l = 28; }
    else if (n.indexOf('red') !== -1 || n.indexOf('crimson') !== -1)                                                          { h = 5;   s = 80; l = 45; }
    else if (n.indexOf('blush') !== -1 || n.indexOf('rose') !== -1)                                                           { h = 345; s = 70; l = 80; }
    else if (n.indexOf('hot') !== -1 || n.indexOf('fuchsia') !== -1 || n.indexOf('magenta') !== -1)                           { h = 320; s = 90; l = 55; }
    else if (n.indexOf('pink') !== -1)                                                                                         { h = 340; s = 75; l = 75; }
    else if (n.indexOf('plum') !== -1 || n.indexOf('aubergine') !== -1)                                                       { h = 290; s = 60; l = 28; }
    else if (n.indexOf('purple') !== -1 || n.indexOf('violet') !== -1)                                                        { h = 270; s = 65; l = 55; }
    else if (n.indexOf('lavender') !== -1 || n.indexOf('lilac') !== -1 || n.indexOf('mauve') !== -1)                          { h = 260; s = 40; l = 80; }
    else if (n.indexOf('midnight') !== -1 || n.indexOf('navy') !== -1)                                                        { h = 225; s = 70; l = 18; }
    else if (n.indexOf('royal') !== -1 || n.indexOf('cobalt') !== -1)                                                         { h = 225; s = 75; l = 45; }
    else if (n.indexOf('denim') !== -1)                                                                                        { h = 215; s = 60; l = 42; }
    else if (n.indexOf('sky') !== -1 || n.indexOf('powder') !== -1 || n.indexOf('baby blue') !== -1)                          { h = 200; s = 65; l = 75; }
    else if (n.indexOf('teal') !== -1 || n.indexOf('turquoise') !== -1 || n.indexOf('aqua') !== -1)                           { h = 175; s = 70; l = 50; }
    else if (n.indexOf('petrol') !== -1 || n.indexOf('duck') !== -1)                                                          { h = 185; s = 55; l = 28; }
    else if (n.indexOf('mint') !== -1 || n.indexOf('sage') !== -1)                                                            { h = 150; s = 45; l = 72; }
    else if (n.indexOf('lime') !== -1)                                                                                         { h = 90;  s = 75; l = 48; }
    else if (n.indexOf('green') !== -1)                                                                                        { h = 130; s = 60; l = 42; }
    else if (n.indexOf('blue') !== -1)                                                                                         { h = 215; s = 70; l = 50; }
    else if (n.indexOf('silver') !== -1 || n.indexOf('grey') !== -1 || n.indexOf('gray') !== -1)                              { h = 0;   s = 0;  l = 65; }
    else if (n.indexOf('charcoal') !== -1 || n.indexOf('black') !== -1)                                                       { h = 0;   s = 0;  l = 14; }
    else if (n.indexOf('brown') !== -1 || n.indexOf('chocolate') !== -1)                                                      { h = 25;  s = 60; l = 28; }
    else {
      /* Unknown name: deterministic hash so the same name always produces
         the same non-white colour and the result is never transparent. */
      var hash = 0;
      for (var i = 0; i < n.length; i++) { hash = ((hash << 5) - hash + n.charCodeAt(i)) | 0; }
      h = Math.abs(hash) % 360;
      s = 55;
      l = 50;
    }
    return 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
  }

  /* Applies nameToColour() to any swatch whose CSS class didn't resolve
     to a background colour (transparent = no matching cl-swatch--* rule). */
  function initSwatchColours() {
    var page = document.getElementById('campaign-landing');
    if (!page) return;

    page.querySelectorAll('.cl-hero__color-swatch, .cl-color-chip__swatch').forEach(function (sw) {
      var bg = window.getComputedStyle(sw).backgroundColor;
      /* 'rgba(0, 0, 0, 0)' / 'transparent' = no CSS colour class matched */
      if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') return;

      /* Colour name from data-colour attribute */
      var name = sw.getAttribute('data-colour') || '';

      /* Fallback for chip swatches: read the sibling name span */
      if (!name) {
        var chip = sw.closest('.cl-color-chip');
        if (chip) {
          var nameEl = chip.querySelector('.cl-color-chip__name');
          if (nameEl) name = nameEl.textContent.trim();
        }
      }

      if (!name) return;
      sw.style.background = nameToColour(name);
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     STICKY ADD TO CART BAR — full-featured compact purchase panel
     ───────────────────────────────────────────────────────────────────────
     Architecture: ONE source of truth — the main hero form.

     Sticky bar ──► main form:  every selector click in the sticky bar
       delegates to .click() on the matching main form element, so all
       existing event handlers (selectColour, applyPack, size buttons) run
       exactly as if the user clicked the main form directly.

     Main form ──► sticky bar:  MutationObservers on the main form elements
       detect any class / content changes and reflect them back to the sticky
       bar's compact controls in real time.

     ATC: clicking the sticky button fires the original #cl-atc-btn click,
       which owns all variant resolution / /cart/add.js / checkout logic.
     ═══════════════════════════════════════════════════════════════════════ */
  function initStickyAtc() {
    /* ── Core DOM refs ───────────────────────────────────────────────── */
    var bar        = document.getElementById('cl-satc');
    var ctaTarget  = document.querySelector('.cl-hero__cta');
    if (!bar || !ctaTarget) return;

    var sThumb      = document.getElementById('cl-satc-thumb');
    var sPriceEl    = document.getElementById('cl-satc-price');   /* new price display */
    var sQtyVal     = document.getElementById('cl-satc-qty');
    var sMinus      = document.getElementById('cl-satc-minus');
    var sPlus       = document.getElementById('cl-satc-plus');
    var sBtn        = document.getElementById('cl-satc-btn');
    var sLabel      = document.getElementById('cl-satc-label');

    var variantSel  = document.getElementById('cl-satc-variant');

    /* Main form refs */
    var mainQtyVal   = document.querySelector('.cl-hero__qty-val');
    var mainColorEl  = document.querySelector('.cl-hero__color-name');
    var mainPriceEl  = document.getElementById('cl-price-ws');    /* source of truth for price */
    var mainGallImg  = document.getElementById('cl-gallery-main-img');
    var mainBtn      = document.getElementById('cl-atc-btn');

    /* ── Colour order and variant data ──────────────────────────────────── */
    var COLOUR_ORDER = [
      'Coral', 'Red', 'Light Grey', 'Mocha', 'Dark Brown', 'Fuchsia', 'Cream', 'Beige',
      'Neon Pink', 'Denim Blue', 'Dusty Pink', 'Neon Orange', 'Royal Blue', 'Navy Blue',
      'Dark Grey', 'White', 'Jade Green', 'Gucci Green', 'Black', 'Lemon', 'Lilac',
      'Sky Blue', 'Mustard Yellow', 'Dark Green', 'Grey'
    ];

    var satcVariants = (function () {
      var el = document.getElementById('cl-variant-data');
      if (!el) return [];
      try { return JSON.parse(el.textContent || '[]'); } catch (e) { return []; }
    }());

    /* Build dropdown grouped by COLOUR_ORDER, Pack of 1 then Pack of 3.
       Format: "Coral / Pack of 1 / In Stock" */
    function buildVariantSelect() {
      if (!variantSel) return;
      variantSel.innerHTML = '';
      COLOUR_ORDER.forEach(function (colour) {
        [1, 3].forEach(function (packNum) {
          var packStr = 'Pack of ' + packNum;
          var cLow = colour.toLowerCase(), pLow = packStr.toLowerCase();
          var found = null;
          for (var i = 0; i < satcVariants.length; i++) {
            var v = satcVariants[i];
            if ((v.o1 || '').toLowerCase() === cLow && (v.o2 || '').toLowerCase() === pLow) {
              found = v; break;
            }
          }
          if (!found) return;
          var opt = document.createElement('option');
          opt.value          = String(found.id);
          opt.dataset.colour = colour;
          opt.dataset.pack   = String(packNum);
          opt.textContent    = colour + ' / ' + packStr + ' / ' + (found.avail ? 'In Stock' : 'Out of Stock');
          variantSel.appendChild(opt);
        });
      });

      variantSel.addEventListener('change', function () {
        var opt = variantSel.options[variantSel.selectedIndex];
        if (!opt) return;
        var tColour = opt.dataset.colour;
        var tPack   = opt.dataset.pack;
        document.querySelectorAll('.cl-hero__color-swatch').forEach(function (sw) {
          if ((sw.getAttribute('title') || '') === tColour) sw.click();
        });
        document.querySelectorAll('.cl-hero__pack-btn').forEach(function (pb) {
          if (pb.dataset.pack === tPack) pb.click();
        });
      });
    }

    /* After API fetch, refresh stock labels on existing options. */
    function updateAvailability(apiVariants) {
      if (!variantSel) return;
      Array.from(variantSel.options).forEach(function (opt) {
        var colour  = opt.dataset.colour || '';
        var packNum = opt.dataset.pack   || '1';
        var packStr = 'Pack of ' + packNum;
        var cLow = colour.toLowerCase(), pLow = packStr.toLowerCase();
        var available = false;
        for (var i = 0; i < apiVariants.length; i++) {
          var v = apiVariants[i];
          if ((v.option1 || '').toLowerCase() === cLow &&
              (v.option2 || '').toLowerCase() === pLow) {
            available = !!v.available;
            break;
          }
        }
        opt.textContent = colour + ' / ' + packStr + ' / ' + (available ? 'In Stock' : 'Out of Stock');
      });
    }

    /* Keep dropdown in sync when colour/pack changes in the main form. */
    function syncVariantSel() {
      if (!variantSel) return;
      var activeSwatch = document.querySelector('.cl-hero__color-swatch--active');
      var activePack   = document.querySelector('.cl-hero__pack-btn--active');
      var colour  = activeSwatch ? (activeSwatch.getAttribute('title') || '') : '';
      var packNum = activePack   ? String(parseInt(activePack.dataset.pack, 10) || 1) : '1';
      for (var i = 0; i < variantSel.options.length; i++) {
        var opt = variantSel.options[i];
        if (opt.dataset.colour === colour && opt.dataset.pack === packNum) {
          variantSel.selectedIndex = i;
          break;
        }
      }
    }

    /* ── Sync sticky display ← main form ───────────────────────────── */

    function syncPrice() {
      if (!sPriceEl || !mainPriceEl) return;
      sPriceEl.textContent = mainPriceEl.textContent;
    }

    function syncThumb() {
      if (!sThumb || !mainGallImg) return;
      var src = mainGallImg.getAttribute('src');
      if (src && src !== sThumb.getAttribute('src')) {
        sThumb.style.opacity = '0';
        sThumb.setAttribute('src', src);
        sThumb.onload = function () { sThumb.style.opacity = '1'; };
        setTimeout(function () { sThumb.style.opacity = '1'; }, 200);
      }
    }

    /* Master sync: variant dropdown + price */
    function syncAll() {
      syncVariantSel();
      syncPrice();
    }

    function syncQtyFromMain() {
      if (mainQtyVal && sQtyVal) sQtyVal.value = mainQtyVal.value;
    }

    /* ── Intersection Observer ───────────────────────────────────────── */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          bar.classList.remove('is-visible');
          bar.setAttribute('aria-hidden', 'true');
        } else if (window.scrollY > 80) {
          syncAll();
          syncQtyFromMain();
          syncThumb();
          bar.classList.add('is-visible');
          bar.setAttribute('aria-hidden', 'false');
        }
      });
    }, { threshold: 0 });

    io.observe(ctaTarget);

    /* ── Sticky qty controls ─────────────────────────────────────────── */
    function readQty() { return parseInt(sQtyVal ? sQtyVal.value : '1', 10) || 1; }
    function setQty(n) {
      var val = Math.max(1, n);
      if (sQtyVal)    sQtyVal.value    = val;
      if (mainQtyVal) mainQtyVal.value = val;
    }
    if (sMinus) sMinus.addEventListener('click', function () { setQty(readQty() - 1); });
    if (sPlus)  sPlus.addEventListener('click',  function () { setQty(readQty() + 1); });
    if (sQtyVal) {
      sQtyVal.addEventListener('input', function () {
        var n = parseInt(sQtyVal.value, 10);
        if (!n || n < 1) sQtyVal.value = 1;
        if (mainQtyVal) mainQtyVal.value = sQtyVal.value;
      });
      sQtyVal.addEventListener('blur', function () {
        var n = parseInt(sQtyVal.value, 10);
        if (!n || n < 1) { sQtyVal.value = 1; if (mainQtyVal) mainQtyVal.value = 1; }
      });
    }

    /* ── Sticky ATC button ───────────────────────────────────────────── */
    if (sBtn && mainBtn) {
      sBtn.addEventListener('click', function () {
        setQty(readQty()); /* ensure qty is synced before main button reads it */

        /* Mirror loading / error / ready states from main button */
        var stateObs = new MutationObserver(function (muts) {
          muts.forEach(function (m) {
            if (m.attributeName !== 'class') return;
            var cls = mainBtn.className;
            if (cls.indexOf('cl-btn--loading') !== -1) {
              sBtn.classList.add('is-loading');
              if (sLabel) sLabel.textContent = 'Adding…';
            } else if (cls.indexOf('cl-btn--error') !== -1) {
              sBtn.classList.remove('is-loading');
              sBtn.classList.add('is-error');
              if (sLabel) sLabel.textContent = 'Error — retry';
              setTimeout(function () {
                sBtn.classList.remove('is-error');
                if (sLabel) sLabel.textContent = 'Add To Cart';
              }, 4000);
              stateObs.disconnect();
            } else {
              sBtn.classList.remove('is-loading', 'is-error');
              if (sLabel) sLabel.textContent = 'Add To Cart';
              stateObs.disconnect();
            }
          });
        });
        stateObs.observe(mainBtn, { attributes: true, attributeFilter: ['class'] });
        mainBtn.click(); /* all cart logic lives here — we just trigger it */
      });
    }

    /* ── MutationObservers: main form → sticky bar ───────────────────── */

    /* Colour name text → variant label + swatch active state */
    if (mainColorEl) {
      new MutationObserver(syncAll)
        .observe(mainColorEl, { characterData: true, childList: true, subtree: true });
    }

    /* Colour swatch active class → variant summary */
    var swatchesContEl = document.querySelector('.cl-hero__color-swatches');
    if (swatchesContEl) {
      new MutationObserver(syncAll)
        .observe(swatchesContEl, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }

    /* Price text → sticky price display
       #cl-price-ws is updated by applyPack() when pack changes, so this
       observer fires automatically whenever the pack (or variant) changes. */
    if (mainPriceEl) {
      new MutationObserver(syncPrice)
        .observe(mainPriceEl, { characterData: true, childList: true, subtree: true });
    }

    /* Size button active class → size select + variant label */
    var sizeBtnsEl = document.querySelector('.cl-hero__size-btns');
    if (sizeBtnsEl) {
      new MutationObserver(syncAll)
        .observe(sizeBtnsEl, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }

    /* Pack button active class → pack pills + variant label */
    var packBtnsEl = document.querySelector('.cl-hero__pack-btns');
    if (packBtnsEl) {
      new MutationObserver(syncAll)
        .observe(packBtnsEl, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }

    /* Main qty value → sticky qty */
    if (mainQtyVal) {
      mainQtyVal.addEventListener('input', syncQtyFromMain);
    }

    /* Gallery image src → sticky thumbnail */
    if (mainGallImg) {
      new MutationObserver(syncThumb)
        .observe(mainGallImg, { attributes: true, attributeFilter: ['src'] });
    }

    /* Fetch real-time stock status and refresh option labels */
    var satcHandle = mainBtn ? (mainBtn.getAttribute('data-product-handle') || '') : '';
    if (satcHandle) {
      fetch('/products/' + satcHandle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (data) { if (data && data.variants) updateAvailability(data.variants); })
        .catch(function () { /* keep Liquid availability on API failure */ });
    }

    /* ── Build, then initial sync ────────────────────────────────────── */
    buildVariantSelect();
    syncAll();
    syncQtyFromMain();
  }

})();
